import { Spacing } from '@persona-id/design-tokens';
import { EditIcon, ThemeIcon } from '@persona-id/design-tokens/icons';
import Color from 'color';
import { useCallback, useRef, useState } from 'react';
import { ColorResult, SketchPicker } from 'react-color';
import { UseFormSetValue } from 'react-hook-form';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import GraphVertexIcon from 'dashboard/components/GraphVertexIcon';
import Tooltip from 'dashboard/components/Tooltip';
import {
  DatumType,
  DatumTypeToken,
  DatumValueType,
  GraphAttributes,
  GraphProperty,
  NodeDataTypeIcon,
  NodeRenderConfig,
} from 'dashboard/models/graph';
import { ServerNodeRenderConfig } from 'dashboard/models/graph-backend';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { MenuDivider } from 'design-system/components/Menu';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { BASE } from 'design-system/variables/spacing';
import { ACCOUNT_DATUM_TYPE } from 'lib/constants/graph';
import useContainerTargetDetector from 'lib/hooks/useContainerTargetDetector';
import { useEscListener } from 'lib/hooks/useKeyListener';

interface GraphNodeConfigurationRowProps {
  config: ServerNodeRenderConfig;
  index: number;
  setValue: UseFormSetValue<GraphAttributes>;
  customDatumTypes: Map<DatumTypeToken, DatumType>;
  defaultGraphProperties: GraphProperty[];
  onGraphConfigEdit: () => void;
  // NOTE(unzi): A temporary prop while we migrate all datum types to use node functions
  hasNodeFunctionsConfig: boolean;
}

const RowContainer = styled('div')`
  :hover {
    background-color: ${({ theme }) => theme.color.background.highlight};
  }
`;
const Row = styled('div')`
  width: 100%;
  display: flex;
  align-items: top;
  margin: ${Spacing[1]};
  column-gap: 20px;
  padding: 4px ${BASE * 0.5 + 10}px 4px 4px;
  cursor: pointer;
  :hover {
    background-color: ${({ theme }) => theme.color.background.highlight};
  }
`;
const SketchPickerContainer = styled('div')`
  top: 225px;
  right: ${Spacing[2]};
  z-index: ${Z_INDICES.toast};
  position: 'absolute';
`;
const Divider = styled(MenuDivider)`
  margin: 0;
`;

const SketchPickerTooltip = styled(Tooltip)`
  box-shadow: none;
  padding: 0;
  margin-left: auto;
`;

const IconPickerContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  width: 190px;
  padding: ${Spacing[1]};
`;

const IconContainer = styled(Button)`
  margin: 1px;
  padding: 0px 5px;
`;

const TooltipContainer = styled('div')`
  margin-left: auto;
`;

export const GraphNodeConfigurationRow = ({
  config,
  index,
  setValue,
  customDatumTypes,
  defaultGraphProperties,
  onGraphConfigEdit,
  hasNodeFunctionsConfig,
}: GraphNodeConfigurationRowProps) => {
  const navigate = useNavigate();
  const hasHoverCardFields = defaultGraphProperties.length > 0;
  const isEmailSimilarity =
    customDatumTypes.has(config['datum-type-token']) &&
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    customDatumTypes.get(config['datum-type-token']).valueType === DatumValueType.EmailSimilarity;
  // NOTE: Not all datum types have configs. Only those that use node functions, have hover card
  // attributes, and/or are of email similarity type.
  const hasNodeConfigPage = hasNodeFunctionsConfig || hasHoverCardFields || isEmailSimilarity;

  const [hover, setHover] = useState(false);

  // state for tracking color picker menu
  const [colorPickeractive, setColorPickerActive] = useState(false);
  const colorPickerEl = useRef(null);
  const colorButtonEl = useRef(null);

  // state for tracking icon picker menu
  const [iconPickerActive, setIconPickerActive] = useState(false);
  const iconPickerEl = useRef(null);
  const iconButtonEl = useRef(null);

  useContainerTargetDetector(() => setColorPickerActive(false), colorButtonEl, colorPickerEl);
  useEscListener(() => setColorPickerActive(false));

  useContainerTargetDetector(() => setIconPickerActive(false), iconPickerEl, iconButtonEl);
  useEscListener(() => setIconPickerActive(false));

  const handlePickColor = useCallback(
    (color: Partial<ColorResult>) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setValue && setValue(`node-render-configs.${index}.color`, color.hex.toUpperCase());
      onGraphConfigEdit();
    },
    [setValue, index],
  );
  const handlePickIcon = useCallback(
    (icon: Partial<string>) => {
      setValue && setValue(`node-render-configs.${index}.icon`, icon as NodeDataTypeIcon);
      setIconPickerActive(false);
      onGraphConfigEdit();
    },
    [setValue, index],
  );

  const renderColorPickerMenu = () => {
    return (
      <SketchPickerContainer ref={colorPickerEl}>
        <SketchPicker
          color={config.color}
          disableAlpha={true}
          presetColors={[]}
          onChangeComplete={handlePickColor}
        />
      </SketchPickerContainer>
    );
  };

  const renderIconPickerMenu = () => {
    return (
      <IconPickerContainer ref={iconPickerEl}>
        {Object.values(NodeDataTypeIcon).map((iconName, index) => (
          <IconContainer
            key={index}
            onClick={() => handlePickIcon(iconName)}
            width='auto'
            aria-label='Change Color'
            type='button'
            size='sm'
            variant='inverse'
            id='graph__change_node_icon_selection'
          >
            <GraphVertexIcon
              version='v2'
              datumTypeToken={config['datum-type-token']}
              nodeRenderConfig={
                {
                  color: Color(config.color),
                  icon: iconName,
                  displayName: config['display-name'],
                  placeHolder: config['search-placeholder'],
                } as NodeRenderConfig
              }
            />
          </IconContainer>
        ))}
      </IconPickerContainer>
    );
  };

  return (
    <>
      <RowContainer>
        <Row
          onClick={() => {
            if (hasNodeConfigPage) {
              navigate(`/dashboard/graph-configuration/${config['datum-type-token']}`);
            }
          }}
          id='graph__context-menu-show'
          onMouseEnter={(e) => {
            e.stopPropagation();
            setHover(true);
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            setHover(false);
          }}
        >
          <Spacer width={0} height={3} />

          <GraphVertexIcon
            version='v2'
            datumTypeToken={config['datum-type-token']}
            nodeRenderConfig={
              {
                color: Color(config.color),
                icon: config.icon,
                displayName: config['display-name'],
                placeHolder: config['search-placeholder'],
              } as NodeRenderConfig
            }
          />
          <Paragraph color='primary'>{config['display-name']}</Paragraph>

          {hover && (
            <>
              <TooltipContainer>
                <SketchPickerTooltip
                  placement='left-start'
                  hideArrow={true}
                  tooltipShown={colorPickeractive}
                  tooltip={renderColorPickerMenu()}
                >
                  <Button
                    width='icon'
                    aria-label='Change Color'
                    type='button'
                    ref={colorButtonEl}
                    onClick={(e) => {
                      e.stopPropagation();
                      setColorPickerActive(true);
                    }}
                    size='sm'
                    variant='inverse'
                    id='graph__change_node_color'
                    disabled={config['datum-type-token'] === ACCOUNT_DATUM_TYPE.token}
                  >
                    <ThemeIcon size={5} />
                  </Button>
                </SketchPickerTooltip>
              </TooltipContainer>
              <SketchPickerTooltip
                placement='left-start'
                hideArrow={true}
                tooltipShown={iconPickerActive}
                tooltip={renderIconPickerMenu()}
              >
                <Button
                  width='icon'
                  aria-label='Change Icon'
                  type='button'
                  ref={iconButtonEl}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIconPickerActive(true);
                  }}
                  size='sm'
                  variant='inverse'
                  id='graph__change_node_icon'
                >
                  <EditIcon size={5} />
                </Button>
              </SketchPickerTooltip>
            </>
          )}
        </Row>
      </RowContainer>
      <Divider />
    </>
  );
};

export default GraphNodeConfigurationRow;
